/**
 * localStorage that cannot take the app down with it.
 *
 * THE PROBLEM
 *
 * `localStorage.setItem` is not safe to call. It throws, synchronously, in
 * situations the app has no control over and the merchant may not know they
 * are in:
 *
 *   • Safari in private browsing, historically on every write
 *   • Chrome with "block third-party cookies" — which also blocks storage for
 *     an embedded iframe, and this app is an iframe inside Shopify admin
 *   • any browser with site data blocked for the domain
 *   • quota exceeded
 *
 * Several of those reads and writes happened during the first render — the
 * shop domain, the theme, the language — so the throw did not degrade a
 * feature, it white-screened the app before anything was drawn. TawkChat
 * already wrapped its own calls; nothing else did.
 *
 * It is also what Shopify's App Store review asks about directly: an embedded
 * app "must function properly without relying on third-party cookies or local
 * storage, including when accessed in incognito mode on Chrome".
 *
 * WHAT THIS GUARANTEES
 *
 * Every call answers. A read returns the fallback rather than throwing, and a
 * write reports whether it stuck without making that the caller's problem.
 * Storage becomes a convenience that may silently not be there — which is the
 * only honest way to treat it — so nothing may depend on a value coming back.
 *
 * It deliberately does not cache or shim storage in memory. A fake that
 * remembers within the session would hide exactly the condition this exists to
 * survive, and would make "works in incognito" untestable.
 */

/**
 * Read a key, or the fallback when storage is unavailable or empty.
 *
 * Note `localStorage` itself can throw on property access, not just on the
 * method call, so the reference is inside the try as well.
 *
 * Reached through `globalThis` rather than `window`. In a browser they are
 * the same object, so nothing changes there — but `window` does not exist
 * under the server renderer, which meant every call here hit the catch and
 * returned its fallback. That is safe, and it also made the guard untestable:
 * the render harness installs its own storage on `globalThis`, so a test
 * could never have exercised a blocked read as opposed to a missing `window`.
 */
export function readStored(key, fallback = null) {
  try {
    const v = globalThis.localStorage.getItem(key)
    return v === null ? fallback : v
  } catch {
    return fallback
  }
}

/** Write a key. Returns false when storage refused it, which is not an error. */
export function writeStored(key, value) {
  try {
    globalThis.localStorage.setItem(key, value)
    return true
  } catch {
    return false
  }
}

/** Forget a key. Silent when there was nothing to forget, or no storage. */
export function removeStored(key) {
  try {
    globalThis.localStorage.removeItem(key)
    return true
  } catch {
    return false
  }
}

/**
 * Read and parse JSON, or the fallback.
 *
 * Separate because a stored value that will not parse is the same kind of
 * problem as storage being missing: the caller wants its default and wants to
 * carry on. A half-written or hand-edited value must not throw either.
 */
export function readStoredJson(key, fallback = null) {
  try {
    const raw = globalThis.localStorage.getItem(key)
    if (raw === null) return fallback
    const parsed = JSON.parse(raw)
    return parsed === null || parsed === undefined ? fallback : parsed
  } catch {
    return fallback
  }
}

/** Write JSON. Returns false when storage refused it or it could not serialise. */
export function writeStoredJson(key, value) {
  try {
    globalThis.localStorage.setItem(key, JSON.stringify(value))
    return true
  } catch {
    return false
  }
}

/**
 * The same, for sessionStorage.
 *
 * It fails in exactly the same conditions as localStorage and for the same
 * reasons — blocked site data, private browsing, an iframe with third-party
 * cookies off — so `typeof sessionStorage !== 'undefined'` proves nothing:
 * the object exists and throws on use. That guard was in shopifyFetch and did
 * not protect the call it was wrapped around.
 *
 * Separate from localStorage rather than a flag, because the two mean
 * different things: sessionStorage is per-tab and is deliberately NOT
 * inherited by a tab opened with noopener, which is the whole reason the
 * admin impersonation token needed a second home.
 */
export function readSession(key, fallback = null) {
  try {
    const v = globalThis.sessionStorage.getItem(key)
    return v === null ? fallback : v
  } catch {
    return fallback
  }
}

/** Write a key for this tab only. Returns false when storage refused it. */
export function writeSession(key, value) {
  try {
    globalThis.sessionStorage.setItem(key, value)
    return true
  } catch {
    return false
  }
}

/** Forget a per-tab key. Silent when there was nothing to forget. */
export function removeSession(key) {
  try {
    globalThis.sessionStorage.removeItem(key)
    return true
  } catch {
    return false
  }
}

/**
 * Whether storage works at all, by trying it rather than feature-detecting.
 *
 * `'localStorage' in window` is true in every case above — the object exists
 * and throws on use — so presence proves nothing and a write is the only real
 * test. Intended for diagnostics, not for guarding calls: the helpers above
 * are already safe, and checking first would be a race as well as a waste.
 */
export function storageWorks() {
  const probe = '__sb_probe__'
  try {
    globalThis.localStorage.setItem(probe, '1')
    globalThis.localStorage.removeItem(probe)
    return true
  } catch {
    return false
  }
}
